import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Shirt, Eye, EyeOff } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [username, setUsername] = useState("");
  const [pin, setPin] = useState("");
  const [showPin, setShowPin] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgot, setForgot] = useState(false);
  const [forgotId, setForgotId] = useState("");
  const [forgotMsg, setForgotMsg] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);

  const submitForgot = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    setForgotMsg("");
    try {
      const { data } = await api.post("/auth/forgot-password", { identifier: forgotId, origin: window.location.origin });
      setForgotMsg(data.message || "Si un compte existe, un email a été envoyé.");
    } catch (err) {
      setForgotMsg(apiError(err));
    } finally {
      setForgotLoading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(username, pin);
      nav("/");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-gradient-to-br from-sky-600 to-sky-800 p-4">
      <div className="w-full max-w-sm bg-white rounded-2xl shadow-xl p-8">
        <div className="flex flex-col items-center mb-6">
          <div className="h-16 w-16 rounded-2xl bg-sky-500 text-white grid place-items-center mb-3 shadow-lg shadow-sky-500/30">
            <Shirt size={30} />
          </div>
          <h1 className="font-display text-3xl font-extrabold text-slate-900">WuraPro</h1>
          <p className="text-slate-500 text-sm">Gestion de pressing</p>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="username">Nom d'utilisateur</Label>
            <Input id="username" data-testid="login-username" value={username} onChange={(e) => setUsername(e.target.value)} placeholder="ex: admin" required />
          </div>
          <div>
            <Label htmlFor="pin">Code PIN</Label>
            <div className="relative">
              <Input id="pin" data-testid="login-pin" type={showPin ? "text" : "password"} value={pin} onChange={(e) => setPin(e.target.value)} placeholder="••••" required className="pr-10" />
              <button type="button" data-testid="toggle-pin-visibility" onClick={() => setShowPin(!showPin)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" aria-label={showPin ? "Masquer" : "Afficher"}>
                {showPin ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>
          {error && <p data-testid="login-error" className="text-sm text-red-600">{error}</p>}
          <Button type="submit" data-testid="login-submit" disabled={loading} className="w-full bg-sky-600 hover:bg-sky-700">
            {loading ? "Connexion..." : "Se connecter"}
          </Button>
        </form>
        <div className="mt-5 pt-4 border-t border-slate-100 text-center">
          <p className="text-sm text-slate-500">Connectez-vous avec votre identifiant et votre mot de passe.</p>
          <button type="button" data-testid="forgot-password-btn" onClick={() => setForgot(true)} className="mt-2 text-sm font-medium text-sky-600 hover:text-sky-700">
            Mot de passe oublié ?
          </button>
        </div>
      </div>

      <Dialog open={forgot} onOpenChange={(o) => { setForgot(o); if (!o) { setForgotMsg(""); setForgotId(""); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mot de passe oublié</DialogTitle>
            <DialogDescription>
              Saisissez votre identifiant ou votre email. Si un compte avec une adresse email existe,
              vous recevrez un lien de réinitialisation (valable 1 heure).
            </DialogDescription>
          </DialogHeader>
          {forgotMsg ? (
            <div className="space-y-4">
              <p data-testid="forgot-message" className="text-sm text-emerald-700 bg-emerald-50 rounded-lg p-3">{forgotMsg}</p>
              <Button onClick={() => { setForgot(false); setForgotMsg(""); setForgotId(""); }} className="w-full bg-sky-600 hover:bg-sky-700">Fermer</Button>
            </div>
          ) : (
            <form onSubmit={submitForgot} className="space-y-3">
              <div>
                <Label htmlFor="forgot-id">Identifiant ou email</Label>
                <Input id="forgot-id" data-testid="forgot-identifier" value={forgotId} onChange={(e) => setForgotId(e.target.value)} placeholder="ex: marie ou marie@email.com" required />
              </div>
              <Button type="submit" data-testid="forgot-submit" disabled={forgotLoading} className="w-full bg-sky-600 hover:bg-sky-700">
                {forgotLoading ? "Envoi..." : "Envoyer le lien"}
              </Button>
              <p className="text-xs text-slate-400 text-center">Sinon, demandez à un administrateur de réinitialiser votre mot de passe.</p>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
